export const en = {
  brokerage: {
    loads: {
      title: 'Brokered Loads',
      filters: {
        search: {
          aria: 'Search brokered loads',
          placeholder: 'Search loads...',
        },
        clearAll: 'Clear All',
      },
      toolbar: {
        create: 'Create Load',
        void: 'Void',
        selected: 'selected',
      },
      table: {
        aria: 'Brokered loads list',
        columns: {
          expectedShipDate: 'Expected Ship Date',
          loadNumber: 'Load #',
          supplier: 'Supplier',
          brokeredOrder: 'Brokered Order',
          salesOrderNumber: 'Sales Order',
          lineItemCount: 'Items',
          netQuantity: 'Net Qty',
          shippedTotal: 'Shipped Total',
          status: 'Status',
          shippedDate: 'Shipped Date',
          createdOn: 'Created',
        },
      },
      emptyState: {
        title: 'No brokered loads',
        subtitle: 'Click Create Load to create your first brokered load.',
        action: 'Create Load',
      },
      columnFilters: {
        dateRange: {
          label: 'Date Range',
          aria: 'Filter by date range',
          clear: 'Clear',
          done: 'Done',
        },
        facility: {
          searchPlaceholder: 'Search facilities...',
        },
        supplier: {
          searchPlaceholder: 'Search suppliers...',
        },
        status: {
          searchPlaceholder: 'Search statuses...',
        },
      },
      emptyResultsState: {
        title: 'No results found',
        subtitle: 'Try adjusting your search or filters.',
        action: (filter?: string) =>
          filter ? `Clear "${filter}"` : 'Clear Filters',
      },
      content: {
        error: (message: string) => `Error loading brokered loads: ${message}`,
      },
      actions: {
        view: 'View',
        markAsShipped: 'Mark as Shipped',
        markAsReceived: 'Mark as Received',
        reconcile: 'Reconcile',
        void: 'Void',
        actions: 'Actions',
      },
      messages: {
        createSuccess: 'Brokered load created successfully',
        createError: 'Failed to create brokered load',
        updateSuccess: 'Brokered load updated successfully',
        updateError: 'Failed to update brokered load',
        documentNumberError: 'Failed to get load number',
        changesDiscarded: 'Changes discarded',
        shippedSuccess: 'Load marked as shipped',
        receivedSuccess: 'Load marked as received',
        reconciledSuccess: 'Load reconciled successfully',
        voidSuccess: (loadNumber: string) =>
          `Load ${loadNumber} voided successfully`,
      },
      dialogs: {
        common: {
          cancel: 'Cancel',
        },
        shipped: {
          title: 'Mark as Shipped',
          shippedDate: 'Shipped Date',
          lightWeight: 'Light Weight',
          heavyWeight: 'Heavy Weight',
          confirm: 'Mark as Shipped',
        },
        received: {
          title: 'Mark as Received',
          receivedDate: 'Received Date',
          confirm: 'Mark as Received',
        },
        void: {
          title: 'Void Load',
          message: (loadNumber: string) =>
            `Are you sure you want to void load ${loadNumber}? This action cannot be undone.`,
          confirm: 'Void Load',
        },
      },
      create: {
        title: 'Create Brokered Load',
        cancel: 'Cancel',
        submit: 'Create Load',
        wizard: {
          steps: {
            shipmentInfo: 'Shipment Info',
            materials: 'Materials',
          },
          buttons: {
            back: 'Back',
            next: 'Next',
          },
        },
        fields: {
          loadNumber: 'Load #',
          supplier: 'Supplier',
          brokeredOrder: 'Brokered Order',
          expectedShipDate: 'Expected Ship Date',
          facility: 'Facility',
          salesOrder: 'Sales Order',
          booking: 'Booking',
        },
        validation: {
          supplierRequired: 'Supplier is required',
          facilityRequired: 'Facility is required',
          expectedShipDateRequired: 'Expected ship date is required',
        },
      },
      detail: {
        title: (loadNumber: string) => `Load ${loadNumber}`,
        created: 'Created',
        tabs: {
          loadInfo: 'Load Info',
          materials: 'Materials',
          photos: 'Photos',
          documents: 'Documents',
          notes: 'Notes',
        },
        sections: {
          basicInfo: 'Basic Info',
          carrierInfo: 'Carrier Information',
          notes: 'Notes',
        },
        fields: {
          supplier: 'Supplier',
          brokeredOrder: 'Brokered Order #',
          expectedShipDate: 'Expected Ship Date',
          shippedWeights: 'Shipped Weights',
          facility: 'Facility',
          salesOrder: 'Related Sales Order #',
          booking: 'Booking #',
          deliveryNumber: 'Delivery #',
          releaseNumber: 'Release #',
          containerNumber: 'Container #',
          sealNumber: 'Seal #',
          truckNumber: 'Truck #',
          trailerNumber: 'Trailer #',
        },
        footer: {
          materialsCount: (length: number): string =>
            length > 1 ? 'materials' : 'material',
          discard: 'Discard',
          saveUpdates: 'Save Updates',
        },
        materials: {
          aria: 'Load materials list',
          columns: {
            material: 'Materials',
            purchaseOrder: 'Purchase Order',
            quantity: 'Quantity',
            unitPrice: 'Unit Price',
            pricingUnit: 'Unit',
            gross: 'Gross',
            tare: 'Tare',
            net: 'Net',
            requestedWeight: 'Requested Weight',
          },
          addMaterial: 'Add Material',
          addAllFromRequested: 'Add All from Requested',
          addAllFromSalesOrder: 'Add All from Sales Order',
          emptyTitle: 'No materials',
          emptyDescription: 'Add materials to this load',
          emptyShippedTitle: 'No shipped materials',
          emptyShippedDescription: 'Add shipped materials to this load',
          selectMaterial: 'Select material',
          selectPurchaseOrder: 'Select purchase order',
          netWeight: 'Net Weight',
          totalGross: 'Total Gross',
          totalTare: 'Total Tare',
          totalNet: 'Total Net',
          totalValue: 'Total Value',
          phases: {
            requested: 'Requested',
            shipped: 'Shipped',
          },
        },
        photos: {
          empty: 'No photos uploaded',
        },
        documents: {
          empty: 'No documents uploaded',
        },
        notes: {
          internalNotes: 'Internal Notes',
          externalNotes: 'External Notes',
        },
      },
    },
  },

  common: {
    actions: {
      cancel: 'Cancel',
      create: 'Create',
      save: 'Save',
      delete: 'Delete',
      edit: 'Edit',
      void: 'Void',
      add: 'Add',
      link: 'Link',
      back: 'Back',
      discard: 'Discard',
    },
    status: {
      active: 'Active',
      inactive: 'Inactive',
      pending: 'Pending',
      voided: 'Voided',
      open: 'Open',
      inProgress: 'InProgress',
      draft: 'Draft',
      closed: 'Closed',
      fulfilled: 'Fulfilled',
      sent: 'Sent',
      accepted: 'Accepted',
      rejected: 'Rejected',
      expired: 'Expired',
      converted: 'Converted',
      default: 'Default',
    },
    loading: {
      creating: 'Creating...',
      saving: 'Saving...',
      linking: 'Linking...',
      adding: 'Adding...',
      converting: 'Converting...',
    },
    selected: 'selected',
    errors: {
      loading: (entity: string, message: string) =>
        `Error loading ${entity}: ${message}`,
      notFound: (entity: string) => `${entity} not found`,
      unknown: 'An unknown error occurred',
    },
    labels: {
      notes: 'Notes',
      noNotes: 'No notes',
      name: 'Name',
      status: 'Status',
      created: 'Created',
      item: 'Item',
      quantity: 'Quantity',
      unit: 'Unit',
      items: 'Items',
      description: 'Description',
    },
    placeholders: {
      generateNumber: 'Generate number',
      optionalNotes: 'Optional notes...',
      optionalDescription: 'Optional description...',
      searchAccount: 'Search for an account...',
      selectItem: 'Search for an item...',
    },
    sections: {
      additionalInfo: 'Additional Information',
    },
  },

  orders: {
    toolbar: {
      markAsFinished: 'Mark as Finished',
      void: 'Void',
    },
    wizard: {
      steps: {
        contractInfo: 'Contract Info',
        materials: 'Materials',
      },
      buttons: {
        back: 'Back',
        next: 'Next',
      },
    },
    materials: {
      title: 'Materials',
      empty: 'No materials',
      emptyDescription: 'Add materials to define the order pricing.',
      addButton: 'Add Material',
      emptyState: {
        title: 'No materials added yet',
        description:
          'Add materials to define the order pricing and quantities.',
      },
      buttons: {
        addMaterial: 'Add Material',
        addFromOrders: 'Add From Purchase and Sales Orders',
      },
      columns: {
        material: 'Material',
        quantity: 'Quantity',
        unit: 'Unit',
        purchasePrice: 'Purchase Price',
        salesPrice: 'Sales Price',
        avgPurchase: 'Avg Purchase',
        avgSale: 'Avg Sale',
        margin: 'Margin',
        buyUnitPrice: 'Buy Price',
        buyUnitPriceSubheader: 'Per Unit',
        sellUnitPrice: 'Sell Price',
        sellUnitPriceSubheader: 'Per Unit',
        buyRequested: 'Buy Requested',
        receivedWeight: 'Received',
        buyRemaining: 'Buy Remaining',
        sellRequested: 'Sell Requested',
        shippedWeight: 'Shipped',
        remainingTotal: 'Remaining',
        buyTotal: 'Buy Total',
        sellTotal: 'Sell Total',
        estimatedProfit: 'Est. Profit',
      },
      fields: {
        item: 'Item',
        quantity: 'Quantity',
        unit: 'Pricing Unit',
        purchasePrice: 'Purchase Price',
        salesPrice: 'Sales Price',
        purchaseTotal: 'Purchase Total',
        salesTotal: 'Sales Total',
        margin: 'Margin',
        notes: 'Notes',
        actions: 'Actions',
      },
      totals: {
        material: 'Materials',
        totalMaterial: (count: number) =>
          `${count} ${count === 1 ? 'material' : 'materials'}`,
        totalWeight: 'Total Quantity',
        totalBuy: 'Total Buy',
        totalSell: 'Total Sell',
        totalMargin: 'Total Margin',
      },
      placeholders: {
        selectMaterial: 'Select material...',
        selectItem: 'Select item...',
        notes: 'Optional notes for this material...',
      },
      footer: {
        subtotal: 'Subtotal',
        itemCount: (count: number) =>
          `${count} ${count === 1 ? 'item' : 'items'}`,
      },
      toast: {
        added: 'Material added',
        updated: 'Material updated',
        deleted: 'Material deleted',
      },
    },
    list: {
      title: 'Brokered Orders',
      search: 'Search orders...',
      ariaLabel: 'Brokered orders list',
      createButton: 'Create Order',
      empty: {
        title: 'No brokered orders found',
        description:
          'Create your first brokered order to start tracking margins between purchase and sales orders.',
      },
    },
    detail: {
      title: 'Brokered Order',
      orderTitle: (orderNumber: string) => `Order ${orderNumber}`,
      createdAt: 'Created at',
      backButton: 'Back to Orders',
      tabs: {
        overview: 'Overview',
        materials: 'Materials',
        linkedOrders: 'Purchase & Sales Orders',
        notes: 'Notes',
      },
      sections: {
        orderInfo: 'Order Information',
        parties: 'Parties',
        financials: 'Financials',
      },
      fields: {
        orderNumber: 'Order Number',
        status: 'Status',
        item: 'Item',
        quantity: 'Quantity',
        supplier: 'Supplier',
        customer: 'Customer',
        purchaseTotal: 'Purchase Total',
        salesTotal: 'Sales Total',
        margin: 'Margin',
        marginPercent: 'Margin %',
      },
      linkedOrders: {
        purchaseOrders: 'Linked Purchase Orders',
        salesOrders: 'Linked Sales Orders',
        noPurchaseOrders: 'No purchase orders linked',
        noSalesOrders: 'No sales orders linked',
        linkPurchaseOrder: 'Link Purchase Order',
        linkSalesOrder: 'Link Sales Order',
        searchPlaceholder: 'Search orders...',
        purchaseOrdersCount: (count: number) => `Purchase Orders (${count})`,
        salesOrdersCount: (count: number) => `Sales Orders (${count})`,
        noPurchaseOrdersFound: 'No purchase orders found',
        noSalesOrdersFound: 'No sales orders found',
        selectOrderToView: 'Select an order to view details',
        noOrders: 'No linked orders',
        noOrdersDescription:
          'Link purchase or sales orders to track this brokered transaction.',
      },
      actions: {
        edit: 'Edit',
        openOrder: 'Open Order',
        voidOrder: 'Void Order',
        finishOrder: 'Finish Order',
        revertToOpen: 'Revert to Open',
        printPackingLists: 'Print Packing Lists',
        printBillsOfLading: 'Print Bills of Lading',
        downloadPackingLists: 'Download Packing Lists',
        downloadBillsOfLading: 'Download Bills of Lading',
        viewPurchaseOrder: 'View Purchase Order',
        viewSalesOrder: 'View Sales Order',
      },
      footer: {
        discard: 'Discard',
        saveUpdates: 'Save Updates',
      },
      unsavedDataMessage:
        'You have unsaved changes. Are you sure you want to leave?',
    },
    create: {
      title: 'Create Brokered Order',
      description:
        'Create a new brokered order to track margins between purchase and sales orders.',
      sections: {
        orderDetails: 'Order Details',
      },
      submitButton: 'Create Order',
    },
    edit: {
      title: 'Edit Brokered Order',
      sections: {
        orderInfo: 'Order Information',
        parties: 'Parties',
        itemDetails: 'Item Details',
        pricing: 'Pricing',
      },
      fields: {
        orderNumber: 'Order Number',
        supplier: 'Suppliers',
        customer: 'Customers',
      },
      submitButton: 'Save Changes',
    },
    link: {
      title: {
        purchaseOrder: 'Link Purchase Orders',
        salesOrder: 'Link Sales Orders',
      },
      searchPlaceholder: 'Search by order number...',
      filterByAccount: 'Filter by account...',
      createSalesOrder: 'Create Sales Order',
      createPurchaseOrder: 'Create Purchase Order',
      clearSelection: 'Clear',
      availableOrders: (type: 'purchase' | 'sales') =>
        `Available ${type === 'purchase' ? 'Purchase' : 'Sales'} Orders`,
      noOrders: 'No available orders to link',
      ordersSelected: (count: number) => `${count} order(s) selected`,
      submitButton: (count: number) =>
        `Link ${count > 0 ? count : ''} Order${count !== 1 ? 's' : ''}`,
    },
    table: {
      columns: {
        orderNumber: 'Order #',
        status: 'Status',
        supplier: 'Supplier',
        customer: 'Customer',
        lineItems: 'Items',
        purchase: 'Purchase',
        sales: 'Sales',
        margin: 'Margin',
      },
    },
    toast: {
      created: 'Brokered order created successfully',
      updated: 'Brokered order updated successfully',
      voided: 'Brokered order voided successfully',
      opened: 'Brokered order opened successfully',
      finished: 'Brokered order finished successfully',
      reverted: 'Brokered order reverted to open',
      discarded: 'Changes discarded',
      comingSoon: 'This feature is coming soon',
      linkSuccess: (count: number, type: 'purchase' | 'sales') =>
        `${count} ${type} order(s) linked successfully`,
      createError: (error: string) => `Failed to create order: ${error}`,
      updateError: (error: string) => `Failed to update order: ${error}`,
      voidError: (error: string) => `Failed to void order: ${error}`,
      openError: (error: string) => `Failed to open order: ${error}`,
      finishError: (error: string) => `Failed to finish order: ${error}`,
      revertError: (error: string) => `Failed to revert order: ${error}`,
      linkError: (error: string) => `Failed to link orders: ${error}`,
      materialRequired: 'Please select a material for each line item',
    },
    validation: {
      validPurchasePrice: 'Please enter a valid purchase price',
      validSalesPrice: 'Please enter a valid sales price',
      selectOrder: 'Please select at least one order',
      badge: {
        valid: 'Valid',
        invalid: 'Invalid',
      },
      button: {
        validate: 'Validate Order',
        validating: 'Validating...',
      },
      errors: {
        ITEM_NOT_IN_SALES_ORDERS: 'Item is not in any linked sales order',
        ITEM_NOT_IN_PURCHASE_ORDERS: 'Item is not in any linked purchase order',
        INSUFFICIENT_PO_QUANTITY:
          'Insufficient quantity in linked purchase orders',
      },
      section: {
        title: 'Validation',
        issues: 'Validation Issues',
      },
    },
  },

  quotes: {
    types: {
      sales: 'Sales Quote',
      purchase: 'Purchase Quote',
      salesShort: 'Sales',
      purchaseShort: 'Purchase',
      selling: 'Selling',
      buying: 'Buying',
    },
    list: {
      title: 'Quotes',
      search: 'Search quotes...',
      ariaLabel: 'Quotes list',
      createButton: 'Create Quote',
      filters: {
        allStatuses: 'All Statuses',
        allFacilities: 'All Facilities',
        clearAll: 'Clear All',
      },
      empty: {
        title: 'No quotes found',
        description:
          'Create purchase or sales quotes to send to suppliers or customers.',
      },
      emptyResults: {
        title: 'No results found',
        description: 'Try adjusting your search or filters.',
      },
    },
    detail: {
      title: 'Quote',
      quoteTitle: (type: string, quoteNumber: string) =>
        `${type} Quote ${quoteNumber}`,
      salesOrder: 'Sales',
      purchaseOrder: 'Purchase',
      backButton: 'Back to Quotes',
      tabs: {
        overview: 'Quote Info',
        lineItems: 'Materials',
        notes: 'Notes',
      },
      sections: {
        quoteInfo: 'Quote Info',
        paymentInfo: 'Payment Info',
      },
      notes: {
        placeholder: 'Add notes about this quote...',
      },
      fields: {
        quoteNumber: 'Quote Number',
        type: 'Type',
        status: 'Status',
        validUntil: 'Valid Until',
        accountName: 'Name',
        facility: 'Facility',
        subtotal: 'Subtotal',
        paymentCurrency: 'Payment Currency',
        paymentTerm: 'Payment Term',
        shipmentTerm: 'Shipment Term',
        freightCoveredByCustomer: 'Freight covered by Customer',
      },
      actions: {
        edit: 'Edit',
        markInReview: 'Mark In Review',
        markApproved: 'Mark as Approved',
        markRejected: 'Mark as Rejected',
        revertToOpen: 'Revert To Open',
        createSalesOrder: 'Create SO',
        createPurchaseOrder: 'Create PO',
        cloneQuote: 'Clone',
        print: 'Print',
        download: 'Download',
        send: 'Send',
        delete: 'Delete',
        void: 'Void',
        restore: 'Restore',
        viewSalesOrder: 'View Sales Order',
      },
      footer: {
        discard: 'Discard',
        saveUpdates: 'Save Updates',
      },
      unsavedDataMessage:
        'You have unsaved changes. Are you sure you want to leave?',
    },
    create: {
      title: 'Create Quote',
      titleWithType: (type: string) => `Create ${type}`,
      sections: {
        quoteDetails: 'Quote Details',
      },
      fields: {
        quoteType: 'Quote Type',
        account: 'Account',
        customerName: 'Customer Name',
        facility: 'Facility',
        validUntil: 'Quote Deadline',
        shipDate: 'Expected Ship Date',
        terms: 'Terms',
      },
      placeholders: {
        terms: 'Payment terms, delivery terms, etc...',
      },
      submitButton: 'Create Quote',
    },
    edit: {
      title: 'Edit Quote',
      sections: {
        quoteInfo: 'Quote Information',
        validity: 'Validity',
      },
      fields: {
        quoteNumber: 'Quote Number',
        account: 'Account',
        validUntil: 'Quote Deadline',
        shipDate: 'Expected Ship Date',
        terms: 'Terms',
      },
      placeholders: {
        selectAccount: 'Select account...',
        terms: 'Payment terms, delivery terms, etc...',
      },
      submitButton: 'Save Changes',
    },
    clone: {
      title: (quoteNumber: string, type: string) =>
        `Clone ${type} Quote ${quoteNumber}`,
      steps: {
        customers: 'Customers',
        summary: 'Summary',
      },
      searchPlaceholder: 'Search...',
      searchAriaLabel: 'Search accounts',
      noAccounts: 'No accounts found',
      summaryBanner: (count: number) =>
        `After confirming this action, **${count} ${count === 1 ? 'Quote' : 'Quotes'}** will be auto generated **per each Customer**.`,
      summaryFields: {
        facility: 'Facility',
        deadline: 'Deadline',
        totalValue: 'Total Value',
        materials: 'Materials',
        materialsCount: (count: number) =>
          `${count} ${count === 1 ? 'Material' : 'Materials'}`,
        customers: 'Customers',
      },
      buttons: {
        next: 'Next',
        back: 'Back',
        clone: 'Clone',
      },
      noFacility: 'No facility',
      noDeadline: 'No deadline',
      noMaterials: 'No materials',
      unknownItem: 'Unknown',
    },
    convert: {
      approveTitle: 'Approve & Close Quote?',
      approveMessage: (orderType: string) =>
        `The quote will be closed and a ${orderType} will be automatically generated. Do you want to proceed?`,
      approveConfirm: (abbreviation: string) =>
        `Approve & Create ${abbreviation}`,
      approveCancel: 'Discard',
      orderTypes: {
        salesOrder: 'Sales Order',
        purchaseOrder: 'Purchase Order',
        salesOrderShort: 'SO',
        purchaseOrderShort: 'PO',
      },
    },
    lineItems: {
      title: {
        add: 'Add Line Item',
        edit: 'Edit Line Item',
      },
      sections: {
        itemDetails: 'Item Details',
        pricing: 'Pricing',
      },
      fields: {
        item: 'Materials',
        packageFormat: 'Package Format',
        quantity: 'Net Weight',
        unit: 'Pricing Unit',
        unitPrice: 'Unit Price',
        total: 'Est. Total Price',
        currency: 'Currency',
        actions: 'Actions',
      },
      placeholders: {
        selectItem: 'Select item...',
        notesForLineItem: 'Optional notes for this material...',
      },
      empty: 'No materials',
      emptyDescription: 'Add materials to define the quote pricing.',
      addButton: 'Add Material',
      totals: {
        material: 'Materials',
        totalWeight: 'Total Weight',
        estimatedTotal: 'Est. Total',
      },
      footer: {
        subtotal: 'Subtotal',
        itemCount: (count: number) =>
          `${count} ${count === 1 ? 'item' : 'items'}`,
      },
      submitButton: {
        add: 'Add Item',
        save: 'Save Changes',
      },
    },
    table: {
      columns: {
        quoteNumber: 'Quote #',
        type: 'Type',
        status: 'Status',
        account: 'Account',
        customer: 'Customer',
        facility: 'Facility',
        totalMaterials: 'Total Materials',
        totalWeight: 'Total Weight',
        totalValue: 'Total Value',
        truckloads: 'Truckloads',
        quoteDeadline: 'Quote Deadline',
        shipDate: 'Exp. Ship Date',
        notes: 'Notes',
        items: 'Items',
        subtotal: 'Subtotal',
        validUntil: 'Valid Until',
        created: 'Created',
      },
    },
    voidDialog: {
      title: 'Void Quote',
      message: (quoteNumber: string) =>
        `Are you sure you want to void quote ${quoteNumber}?`,
      confirm: 'Void',
      cancel: 'Cancel',
    },
    deleteDialog: {
      title: 'Delete Quote',
      titleBulk: (count: number) =>
        `Delete ${count} ${count === 1 ? 'Quote' : 'Quotes'}`,
      message: (quoteNumber: string) =>
        `Are you sure you want to delete quote ${quoteNumber}? This action cannot be undone.`,
      messageBulk: (count: number) =>
        `Are you sure you want to delete ${count} ${count === 1 ? 'quote' : 'quotes'}? This action cannot be undone.`,
      confirm: 'Delete',
      confirmBulk: (count: number) =>
        `Delete ${count} ${count === 1 ? 'Quote' : 'Quotes'}`,
      cancel: 'Cancel',
      warning: 'This action cannot be undone.',
      quoteLabel: 'Quote #',
    },
    toast: {
      created: 'Quote created successfully',
      updated: 'Quote updated successfully',
      sent: 'Quote sent successfully',
      accepted: 'Quote accepted successfully',
      rejected: 'Quote rejected',
      converted: 'Quote converted successfully',
      reverted: 'Quote reverted to open',
      voided: 'Quote voided successfully',
      bulkVoided: (count: number) =>
        `${count} ${count === 1 ? 'quote' : 'quotes'} voided successfully`,
      discarded: 'Changes discarded',
      lineItemAdded: 'Material item added',
      lineItemUpdated: 'Material item updated',
      lineItemDeleted: 'Material item deleted',
      createError: (error: string) => `Failed to create quote: ${error}`,
      updateError: (error: string) => `Failed to update quote: ${error}`,
      sendError: (error: string) => `Failed to send quote: ${error}`,
      acceptError: (error: string) => `Failed to accept quote: ${error}`,
      rejectError: (error: string) => `Failed to reject quote: ${error}`,
      convertError: (error: string) => `Failed to convert quote: ${error}`,
      voidError: (error: string) => `Failed to void quote: ${error}`,
      lineItemError: (error: string) => `Failed to save line item: ${error}`,
      lineItemDeleteError: (error: string) =>
        `Failed to delete line item: ${error}`,
      materialRequired: 'Please select a material for each line item',
      cloned: (count: number) =>
        `Quote cloned to ${count} ${count === 1 ? 'account' : 'accounts'} successfully`,
      cloneError: (error: string) => `Failed to clone quote: ${error}`,
      restored: 'Quote restored successfully',
      restoreError: (error: string) => `Failed to restore quote: ${error}`,
    },
    validation: {
      selectAccount: 'Please select an account',
    },
  },
};
